<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import TasksAPI from 'dashboard/api/tasks';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import TaskCommentBox from './TaskCommentBox.vue';
import { splitMentions } from './helpers/mentions';

const props = defineProps({
  task: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['commentsChanged']);

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const role = useMapGetter('getCurrentRole');
const isAdmin = computed(() => role.value === 'administrator');

const comments = ref([]);
const editingId = ref(null);
const showAll = ref(false);

const loadComments = async () => {
  const { data } = await TasksAPI.getComments(props.task.id);
  comments.value = data;
};

onMounted(loadComments);

const setComments = list => {
  comments.value = list;
  emit('commentsChanged', list.length);
};

const createComment = async ({ content, mentionedUserIds }) => {
  try {
    const { data } = await TasksAPI.createComment(props.task.id, {
      content,
      mentioned_user_ids: mentionedUserIds,
    });
    setComments([data, ...comments.value]);
  } catch {
    useAlert(t('TASKS.COMMENTS.ERROR'));
    throw new Error('comment');
  }
};

const saveEdit = async (comment, { content, mentionedUserIds }) => {
  try {
    const { data } = await TasksAPI.updateComment(props.task.id, comment.id, {
      content,
      mentioned_user_ids: mentionedUserIds,
    });
    setComments(
      comments.value.map(item => (item.id === data.id ? data : item))
    );
    editingId.value = null;
  } catch {
    useAlert(t('TASKS.COMMENTS.ERROR'));
    throw new Error('comment');
  }
};

const removeComment = async comment => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TASKS.COMMENTS.DELETE_CONFIRM'))) return;
  try {
    await TasksAPI.deleteComment(props.task.id, comment.id);
    setComments(comments.value.filter(item => item.id !== comment.id));
  } catch {
    useAlert(t('TASKS.COMMENTS.ERROR'));
  }
};

const agentNames = computed(() => props.agents.map(agent => agent.name));
const mentionParts = content => splitMentions(content, agentNames.value);
const editedLabel = computed(() => t('TASKS.COMMENTS.EDITED'));
const canEdit = comment => comment.user?.id === currentUser.value?.id;
const canDelete = comment => canEdit(comment) || isAdmin.value;

const historyText = entry => {
  const dash = '—';
  if (entry.type === 'assignee') {
    if (entry.from && entry.to)
      return t('TASKS.ACTIVITY.REASSIGNED', { from: entry.from, to: entry.to });
    if (entry.to) return t('TASKS.ACTIVITY.ASSIGNED', { to: entry.to });
    return t('TASKS.ACTIVITY.UNASSIGNED', { from: entry.from || dash });
  }
  if (entry.type === 'move')
    return t('TASKS.ACTIVITY.MOVED', {
      from: entry.from || dash,
      to: entry.to || dash,
    });
  if (entry.type === 'status') {
    return entry.to === 'done'
      ? t('TASKS.ACTIVITY.COMPLETED')
      : t('TASKS.ACTIVITY.REOPENED');
  }
  if (entry.type === 'member_added')
    return t('TASKS.ACTIVITY.MEMBER_ADDED', { to: entry.to || dash });
  if (entry.type === 'member_removed')
    return t('TASKS.ACTIVITY.MEMBER_REMOVED', { from: entry.from || dash });
  return '';
};

const relativeTime = at => {
  if (!at) return '';
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - at);
  if (diff < 60) return t('TASKS.ACTIVITY.NOW');
  if (diff < 3600)
    return t('TASKS.ACTIVITY.MINUTES', { n: Math.floor(diff / 60) });
  if (diff < 86400)
    return t('TASKS.ACTIVITY.HOURS', { n: Math.floor(diff / 3600) });
  if (diff < 604800)
    return t('TASKS.ACTIVITY.DAYS', { n: Math.floor(diff / 86400) });
  return new Date(at * 1000).toLocaleDateString();
};

const feed = computed(() => {
  const history = (props.task.activities || []).map(entry => ({
    kind: 'history',
    key: `h-${entry.id}`,
    at: entry.at,
    entry,
  }));
  const notes = comments.value.map(comment => ({
    kind: 'comment',
    key: `c-${comment.id}`,
    at: comment.created_at,
    comment,
  }));
  return [...history, ...notes].sort((a, b) => b.at - a.at);
});

const visibleFeed = computed(() =>
  showAll.value ? feed.value : feed.value.slice(0, 5)
);
</script>

<template>
  <div class="flex flex-col gap-3 mb-6">
    <span
      class="text-[11px] font-semibold tracking-wide uppercase text-n-slate-10"
    >
      {{ t('TASKS.ACTIVITY.TITLE') }}
    </span>
    <TaskCommentBox :agents="agents" :on-submit="createComment" />
    <ul
      class="flex flex-col gap-3"
      :class="showAll ? 'max-h-96 overflow-y-auto pr-1' : ''"
    >
      <li
        v-for="item in visibleFeed"
        :key="item.key"
        class="flex items-start gap-2"
      >
        <template v-if="item.kind === 'comment'">
          <Avatar
            :name="item.comment.user?.name || t('TASKS.COMMENTS.REMOVED_USER')"
            :src="item.comment.user?.avatar_url"
            :size="24"
            class="mt-0.5"
          />
          <div class="flex flex-col flex-1 min-w-0 gap-1">
            <p class="text-sm">
              <span class="font-medium text-n-slate-12">{{
                item.comment.user?.name || t('TASKS.COMMENTS.REMOVED_USER')
              }}</span>
              <span class="ml-2 text-xs text-n-slate-10">{{
                relativeTime(item.comment.created_at)
              }}</span>
              <span
                v-if="item.comment.edited_at"
                class="ml-1 text-xs text-n-slate-10"
                >{{ editedLabel }}</span
              >
            </p>
            <TaskCommentBox
              v-if="editingId === item.comment.id"
              :agents="agents"
              :initial-content="item.comment.content"
              :submit-label="t('TASKS.COMMENTS.SAVE')"
              cancelable
              :on-submit="payload => saveEdit(item.comment, payload)"
              @cancel="editingId = null"
            />
            <p
              v-else
              class="px-3 py-2 text-sm whitespace-pre-wrap break-words rounded-lg bg-n-alpha-1 text-n-slate-12"
            >
              <span
                v-for="(part, index) in mentionParts(item.comment.content)"
                :key="index"
                :class="part.mention ? 'font-medium text-n-brand' : ''"
                >{{ part.text }}</span
              >
            </p>
            <div
              v-if="editingId !== item.comment.id"
              class="flex gap-3 text-xs text-n-slate-10"
            >
              <button
                v-if="canEdit(item.comment)"
                class="hover:underline"
                @click="editingId = item.comment.id"
              >
                {{ t('TASKS.COMMENTS.EDIT') }}
              </button>
              <button
                v-if="canDelete(item.comment)"
                class="hover:underline"
                @click="removeComment(item.comment)"
              >
                {{ t('TASKS.COMMENTS.DELETE') }}
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <Avatar :name="item.entry.actor || '?'" :size="24" class="mt-0.5" />
          <div class="flex flex-col min-w-0">
            <p class="text-sm text-n-slate-12">
              <span class="font-medium">{{ item.entry.actor }}</span>
              {{ ' ' }}{{ historyText(item.entry) }}
            </p>
            <span class="text-xs text-n-slate-10">{{
              relativeTime(item.entry.at)
            }}</span>
          </div>
        </template>
      </li>
    </ul>
    <button
      v-if="feed.length > 5"
      class="self-start text-xs font-medium text-n-brand hover:underline"
      @click="showAll = !showAll"
    >
      {{
        showAll
          ? t('TASKS.ACTIVITY.SHOW_LESS')
          : t('TASKS.ACTIVITY.SHOW_ALL', { n: feed.length })
      }}
    </button>
  </div>
</template>
